import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import {
  Users, Search as SearchIcon, CheckCircle2, TrendingUp, Loader2, RefreshCw,
  Smartphone, Monitor, Tablet, Plane, BarChart3, MapPin, Globe, Repeat, Activity,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'traffic', label: 'Traffic', icon: Users },
  { id: 'searches', label: 'Searches', icon: SearchIcon },
  { id: 'bookings', label: 'Bookings', icon: Plane },
  { id: 'funnel', label: 'Funnel', icon: TrendingUp },
  { id: 'cohorts', label: 'Cohorts', icon: Repeat },
  { id: 'suppliers', label: 'Supplier Health', icon: Activity },
];

const COLORS = ['#0d9488', '#f59e0b', '#6366f1', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899'];

const ENDPOINT_KEYS = ['overview', 'traffic', 'searches', 'bookings', 'funnel', 'cohorts', 'suppliers'];

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState('7d');
  const [tab, setTab] = useState('overview');
  const [cohortBasis, setCohortBasis] = useState('booking');
  const [data, setData] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const supplierHealthPeriod = period === '90d' ? '30d' : period;

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    setLoadErrors({});
    try {
      const requests = [
        axios.get(`${API}/admin/analytics/overview`, { withCredentials: true, params: { period } }),
        axios.get(`${API}/admin/analytics/traffic`, { withCredentials: true, params: { period } }),
        axios.get(`${API}/admin/analytics/searches`, { withCredentials: true, params: { period } }),
        axios.get(`${API}/admin/analytics/bookings`, { withCredentials: true, params: { period } }),
        axios.get(`${API}/admin/analytics/funnel`, { withCredentials: true, params: { period } }),
        axios.get(`${API}/admin/analytics/cohorts`, { withCredentials: true, params: { weeks: 8, basis: cohortBasis } }),
        axios.get(`${API}/admin/analytics/supplier-health`, { withCredentials: true, params: { period: supplierHealthPeriod } }),
      ];
      const results = await Promise.allSettled(requests);
      const nextErrors = {};
      results.forEach((result, i) => {
        if (result.status === 'rejected') {
          const key = ENDPOINT_KEYS[i];
          nextErrors[key] = result.reason?.response?.data?.detail
            || result.reason?.message
            || 'Request failed';
        }
      });
      setLoadErrors(nextErrors);
      setData({
        overview: results[0].status === 'fulfilled' ? results[0].value.data : null,
        traffic: results[1].status === 'fulfilled' ? results[1].value.data : null,
        searches: results[2].status === 'fulfilled' ? results[2].value.data : null,
        bookings: results[3].status === 'fulfilled' ? results[3].value.data : null,
        funnel: results[4].status === 'fulfilled' ? results[4].value.data : null,
        cohorts: results[5].status === 'fulfilled' ? results[5].value.data : null,
        suppliers: results[6].status === 'fulfilled' ? results[6].value.data : null,
      });
      if (Object.keys(nextErrors).length > 0) {
        setErr(`Some analytics sections failed to load: ${Object.keys(nextErrors).join(', ')}`);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period, cohortBasis, supplierHealthPeriod]);

  useEffect(() => { load(); }, [load]);

  const kpis = data.overview?.kpis || {};

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Self-hosted metrics for traffic, searches, flight bookings &amp; conversion.
            <span className="block text-xs text-gray-400 mt-0.5">Booking KPIs are flights only (confirmed/ticketed GMV).</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  period === p.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700"
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="analytics-error">
          {err}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard icon={Users} label="Sessions" value={kpis.sessions} loading={loading} />
        <KpiCard icon={Users} label="Unique Users" value={kpis.unique_users} loading={loading} tone="indigo" />
        <KpiCard icon={SearchIcon} label="Searches" value={kpis.searches} loading={loading} tone="amber" />
        <KpiCard icon={Plane} label="Confirmed Bookings" value={kpis.bookings} loading={loading} tone="emerald" />
        <KpiCard icon={CheckCircle2} label="GMV (Confirmed)" value={kpis.gmv ?? kpis.revenue ? `₹${(kpis.gmv ?? kpis.revenue).toLocaleString('en-IN', {maximumFractionDigits:0})}` : '₹0'} loading={loading} tone="emerald" raw />
        <KpiCard icon={TrendingUp} label="Net Revenue" value={kpis.net_revenue != null ? `₹${kpis.net_revenue.toLocaleString('en-IN', {maximumFractionDigits:0})}` : '₹0'} loading={loading} tone="teal" raw />
        <KpiCard icon={TrendingUp} label="Conversion" value={kpis.conversion_pct != null ? `${kpis.conversion_pct}%` : '0%'} loading={loading} tone="rose" raw />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex overflow-x-auto border-b border-gray-100">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition border-b-2 ${
                  tab === t.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
                data-testid={`analytics-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 md:p-6">
          {loading && (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          )}

          {!loading && tab === 'overview' && <OverviewTab data={data.overview} loadError={loadErrors.overview} />}
          {!loading && tab === 'traffic' && <TrafficTab data={data.traffic} loadError={loadErrors.traffic} />}
          {!loading && tab === 'searches' && <SearchesTab data={data.searches} loadError={loadErrors.searches} />}
          {!loading && tab === 'bookings' && <BookingsTab data={data.bookings} loadError={loadErrors.bookings} />}
          {!loading && tab === 'funnel' && <FunnelTab data={data.funnel} loadError={loadErrors.funnel} />}
          {!loading && tab === 'cohorts' && <CohortsTab data={data.cohorts} basis={cohortBasis} onBasisChange={setCohortBasis} loadError={loadErrors.cohorts} />}
          {!loading && tab === 'suppliers' && (
            <SupplierHealthTab
              data={data.suppliers}
              period={supplierHealthPeriod}
              requestedPeriod={period}
              loadError={loadErrors.suppliers}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Subcomponents ──────────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, label, value, loading, tone = 'gray', raw = false }) => {
  const tones = {
    gray: 'bg-white border-gray-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    indigo: 'bg-indigo-50 border-indigo-200',
    rose: 'bg-rose-50 border-rose-200',
  };
  const iconTones = {
    gray: 'text-gray-500',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    indigo: 'text-indigo-600',
    rose: 'text-rose-600',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">{label}</p>
        <Icon className={`w-4 h-4 ${iconTones[tone]}`} />
      </div>
      <p className="text-2xl font-bold mt-2 text-gray-900">
        {loading ? '…' : (raw ? (value ?? '—') : (value != null ? Number(value).toLocaleString('en-IN') : '0'))}
      </p>
    </div>
  );
};

const OverviewTab = ({ data }) => {
  if (!data) return <Empty />;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Daily Trend</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.trend || []}>
            <defs>
              <linearGradient id="gSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gSearches" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gBookings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="sessions" stroke="#0d9488" fill="url(#gSessions)" strokeWidth={2} />
            <Area type="monotone" dataKey="searches" stroke="#f59e0b" fill="url(#gSearches)" strokeWidth={2} />
            <Area type="monotone" dataKey="bookings" stroke="#ef4444" fill="url(#gBookings)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">GMV & Net Revenue (Confirmed)</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.trend || []}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
            <YAxis fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
            <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
            <Legend />
            <Bar dataKey="gmv" fill="#10b981" name="GMV" radius={[4, 4, 0, 0]} />
            <Bar dataKey="net_revenue" fill="#0d9488" name="Net Revenue" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TrafficTab = ({ data }) => {
  if (!data) return <Empty />;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <ChartCard title="Device">
        <PieChartCompact data={data.devices || []} />
      </ChartCard>
      <ChartCard title="User Type">
        <PieChartCompact data={data.user_types || []} />
      </ChartCard>
      <ChartCard title="Top Countries" wide>
        <SimpleList
          rows={(data.countries || []).map(c => ({ label: `${c.code ? c.code + ' · ' : ''}${c.name}`, value: c.count }))}
          emptyText="Geo data appears as visitors come in (public IPs only)"
          testId="top-countries"
        />
      </ChartCard>
      <ChartCard title="Top Cities" wide>
        <SimpleList
          rows={(data.cities || []).map(c => ({ label: c.name, value: c.count }))}
          emptyText="Geo data appears as visitors come in (public IPs only)"
          testId="top-cities"
        />
      </ChartCard>
      <ChartCard title="Top Pages" wide>
        <SimpleList
          rows={(data.top_pages || []).map(p => ({ label: p.path, value: p.count }))}
          emptyText="No pageviews yet"
          testId="top-pages"
        />
      </ChartCard>
      <ChartCard title="Top Referrers" wide>
        <SimpleList
          rows={(data.top_referrers || []).map(r => ({
            label: (() => { try { return new URL(r.referrer).hostname; } catch { return r.referrer; } })(),
            value: r.count,
          }))}
          emptyText="No external referrers yet"
          testId="top-referrers"
        />
      </ChartCard>
      <ChartCard title="Operating System" wide>
        <SimpleList
          rows={(data.os || []).map(o => ({ label: o.name, value: o.count }))}
          emptyText="No OS data"
          testId="os-list"
        />
      </ChartCard>
    </div>
  );
};

const SearchesTab = ({ data }) => {
  if (!data) return <Empty />;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <ChartCard title={`Top Sectors (${data.total_searches} searches)`} wide>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="top-sectors">
              <thead className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <tr>
                  <th className="text-left px-3 py-2">Sector</th>
                  <th className="text-right px-3 py-2">Searches</th>
                  <th className="text-left px-3 py-2">Share</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_sectors || []).map(s => {
                  const pct = data.total_searches ? (s.count / data.total_searches * 100) : 0;
                  return (
                    <tr key={s.sector} className="border-b border-gray-50">
                      <td className="px-3 py-2 font-mono text-xs font-bold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-red-500" />
                        {s.sector}
                      </td>
                      <td className="text-right px-3 py-2 text-gray-700">{s.count.toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-red-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-gray-500 w-10 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!data.top_sectors || data.top_sectors.length === 0) && (
                  <tr><td colSpan={3} className="px-3 py-8 text-center text-gray-400 text-sm">No searches in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
      <div className="space-y-6">
        <ChartCard title="Trip Type">
          <PieChartCompact data={data.trip_type_distribution || []} />
        </ChartCard>
        <ChartCard title="Travel Class">
          <PieChartCompact data={data.class_distribution || []} />
        </ChartCard>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Adults / Search</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data.avg_adults_per_search}</p>
        </div>
      </div>
    </div>
  );
};

const BookingsTab = ({ data, loadError }) => {
  if (loadError) {
    return (
      <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="bookings-load-error">
        Failed to load bookings analytics: {loadError}
      </div>
    );
  }
  if (!data) return <Empty />;
  const confirmed = data.confirmed_bookings ?? data.total_bookings;
  const total = data.total_bookings ?? confirmed;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="lg:col-span-2 text-sm text-gray-600">
        <span className="font-semibold text-gray-900">{confirmed.toLocaleString()} confirmed</span>
        {total !== confirmed && (
          <span>{` · ${total.toLocaleString()} total (all statuses)`}</span>
        )}
        {data.confirmed_gmv != null && (
          <span>{` · GMV ₹${Number(data.confirmed_gmv).toLocaleString('en-IN')}`}</span>
        )}
      </div>
      <ChartCard title={`Supplier Split (${confirmed} confirmed)`}>
        <PieChartCompact data={(data.suppliers || []).map(s => ({ name: s.name, count: s.count }))} />
      </ChartCard>
      <ChartCard title="Booking Status">
        <PieChartCompact data={data.statuses || []} />
      </ChartCard>
      <ChartCard title="GMV by Supplier (Confirmed)" wide>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.suppliers || []} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
            <XAxis type="number" fontSize={11} tickFormatter={(v) => v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`} />
            <YAxis type="category" dataKey="name" fontSize={11} width={80} />
            <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
            <Bar dataKey="revenue" fill="#0d9488" radius={[0,4,4,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="User Type">
        <PieChartCompact data={data.user_types || []} />
      </ChartCard>
      <ChartCard title="Top Routes (by bookings)" wide>
        <SimpleList
          rows={(data.top_routes || []).map(r => ({
            label: r.route,
            value: `${r.count} × ₹${Number(r.revenue).toLocaleString('en-IN')}`,
          }))}
          emptyText="No bookings yet in this period"
          testId="top-routes"
        />
      </ChartCard>
    </div>
  );
};

const FunnelTab = ({ data }) => {
  if (!data) return <Empty />;
  const maxVal = Math.max(1, ...(data.funnel || []).map(s => s.sessions));
  return (
    <div className="space-y-3" data-testid="funnel-list">
      {(data.funnel || []).map((step, i) => {
        const pct = (step.sessions / maxVal) * 100;
        return (
          <div key={step.key} className="flex items-center gap-4">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">{i + 1}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-gray-900">{step.step}</p>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-900">{step.sessions.toLocaleString()} sessions</span>
                  {step.drop_pct != null && step.drop_pct > 0 && (
                    <span className="text-xs font-semibold text-red-600">
                      −{step.drop_pct}% drop
                    </span>
                  )}
                </div>
              </div>
              <div className="h-6 bg-gray-100 rounded overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-end pr-2"
                  style={{ width: `${pct}%` }}
                >
                  {pct > 20 && <span className="text-[10px] font-semibold text-white">{pct.toFixed(1)}%</span>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CohortsTab = ({ data, basis, onBasisChange }) => {
  if (!data) return <Empty />;
  const { labels = [], matrix = [] } = data;
  // Heatmap color based on retention %
  const cellColor = (pct) => {
    if (pct == null) return 'bg-gray-50 text-gray-300';
    if (pct >= 80) return 'bg-emerald-600 text-white';
    if (pct >= 50) return 'bg-emerald-400 text-white';
    if (pct >= 25) return 'bg-emerald-200 text-emerald-900';
    if (pct >= 10) return 'bg-amber-100 text-amber-900';
    if (pct > 0) return 'bg-red-100 text-red-900';
    return 'bg-gray-100 text-gray-400';
  };

  const nonEmpty = matrix.filter(r => r.size > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Weekly Cohort Retention</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Each row is a cohort of users who first {basis === 'booking' ? 'booked' : 'visited'} in that ISO week.
            Columns show the % of that cohort who returned in subsequent weeks.
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg self-start md:self-auto">
          <button
            onClick={() => onBasisChange('booking')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${basis === 'booking' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            data-testid="cohort-basis-booking"
          >
            By Booking
          </button>
          <button
            onClick={() => onBasisChange('session')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${basis === 'session' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            data-testid="cohort-basis-session"
          >
            By Session
          </button>
        </div>
      </div>

      {nonEmpty.length === 0 ? (
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-500">
          <Repeat className="w-6 h-6 mx-auto mb-2 text-gray-300" />
          No repeat-user data yet for the {basis} basis.
          <br />
          <span className="text-xs text-gray-400">
            Cohorts appear once logged-in users return across multiple weeks.
          </span>
        </div>
      ) : (
        <div className="overflow-x-auto bg-gray-50 rounded-xl border border-gray-100 p-2">
          <table className="w-full text-xs" data-testid="cohort-matrix">
            <thead>
              <tr className="text-gray-500 font-semibold">
                <th className="text-left px-2 py-2 whitespace-nowrap">Cohort</th>
                <th className="text-right px-2 py-2">Users</th>
                {labels.map((w, i) => (
                  <th key={w} className="text-center px-2 py-2 whitespace-nowrap">W+{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.cohort} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 font-mono font-bold text-gray-700 whitespace-nowrap">{row.cohort}</td>
                  <td className="px-2 py-1.5 text-right text-gray-600">{row.size}</td>
                  {row.retention.map((pct, i) => (
                    <td key={i} className="p-0.5">
                      <div className={`rounded py-1.5 text-center font-semibold text-[11px] ${cellColor(pct)}`}>
                        {pct == null ? '—' : `${pct}%`}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ChartCard = ({ title, children, wide = false }) => (
  <div className={`bg-gray-50 rounded-xl border border-gray-100 p-4 ${wide ? 'lg:col-span-2' : ''}`}>
    <h3 className="text-sm font-bold text-gray-900 mb-3">{title}</h3>
    {children}
  </div>
);

const PieChartCompact = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={75} label={(e) => `${e.name} (${e.count})`} labelLine={false} fontSize={11}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
};

const SimpleList = ({ rows, emptyText, testId }) => {
  if (!rows || rows.length === 0) {
    return <p className="py-8 text-center text-gray-400 text-sm">{emptyText}</p>;
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid={testId}>
      {rows.map((r, i) => (
        <li key={i} className="flex items-center justify-between py-2 px-1 text-sm">
          <span className="text-gray-800 truncate pr-3 flex-1">{r.label}</span>
          <span className="text-xs font-bold text-gray-500 whitespace-nowrap">{r.value}</span>
        </li>
      ))}
    </ul>
  );
};

const Empty = () => (
  <div className="py-16 text-center text-gray-400 text-sm">No data available for this period.</div>
);

/* ─────────────────────────────────────────────────────────────
 *  SUPPLIER HEALTH TAB
 * ───────────────────────────────────────────────────────────── */
const SUPPLIER_META = {
  tripjack: { color: '#0d9488', label: 'Tripjack' },
  riya:     { color: '#f59e0b', label: 'Riya' },
  offline:  { color: '#6366f1', label: 'Offline' },
};

const SupplierHealthTab = ({ data, period, requestedPeriod, loadError }) => {
  if (loadError) {
    return (
      <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
        Failed to load supplier health: {loadError}
      </div>
    );
  }
  if (!data) return <Empty />;
  const suppliers = data.suppliers || [];
  const timeseries = data.timeseries || {};
  const topFailing = data.top_failing_routes || [];

  // Merge supplier time-series into a single chart dataset keyed by bucket
  const bucketMap = {};
  Object.entries(timeseries).forEach(([supp, rows]) => {
    (rows || []).forEach((r) => {
      const key = r.bucket;
      if (!bucketMap[key]) bucketMap[key] = { bucket: key };
      bucketMap[key][`${supp}_latency`] = r.avg_latency_ms;
      bucketMap[key][`${supp}_err`] = r.err;
    });
  });
  const chartData = Object.values(bucketMap).sort((a, b) =>
    a.bucket < b.bucket ? -1 : 1,
  ).map((r) => ({
    ...r,
    label: (() => {
      const d = new Date(r.bucket);
      return period === '24h'
        ? d.toLocaleTimeString(undefined, { hour: '2-digit' })
        : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    })(),
  }));

  return (
    <div className="space-y-6" data-testid="supplier-health-tab">
      {requestedPeriod === '90d' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Supplier health API supports up to 30 days — showing last 30 days while 90-day period is selected.
        </p>
      )}
      {/* KPI cards per supplier */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {suppliers.map((s) => {
          const meta = SUPPLIER_META[s.name] || { color: '#64748b', label: s.name };
          const rateTone =
            s.success_rate >= 95 ? 'bg-emerald-100 text-emerald-700'
            : s.success_rate >= 80 ? 'bg-amber-100 text-amber-700'
            : 'bg-red-100 text-red-700';
          return (
            <div key={s.name} className="bg-white rounded-xl border border-gray-200 p-5"
                 data-testid={`supplier-card-${s.name}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                  <h3 className="font-semibold text-gray-900">{meta.label}</h3>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${rateTone}`}>
                  {s.success_rate}%
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600">
                <div>
                  <div className="text-gray-400">Total Calls</div>
                  <div className="text-lg font-bold text-gray-900">{s.total_calls.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-400">Avg Latency</div>
                  <div className="text-lg font-bold text-gray-900">{s.avg_latency_ms.toLocaleString()}ms</div>
                </div>
                <div>
                  <div className="text-gray-400">OK / Timeout / Error</div>
                  <div className="font-mono text-gray-800">
                    {s.ok} / <span className="text-amber-600">{s.timeout}</span> / <span className="text-red-600">{s.error}</span>
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">p50 / p95 / p99</div>
                  <div className="font-mono text-gray-800">{s.p50} / {s.p95} / {s.p99}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Latency over time chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Avg Latency Over Time</h3>
          <span className="text-xs text-gray-500">{period}</span>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }}
                     label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {Object.entries(SUPPLIER_META).map(([name, meta]) => (
                <Line key={name} type="monotone" dataKey={`${name}_latency`} name={meta.label}
                      stroke={meta.color} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-400 text-sm py-8">No data yet. Run a few searches to populate.</p>
        )}
      </div>

      {/* Top failing routes */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Top Failing Routes</h3>
        {topFailing.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase border-b">
              <tr>
                <th className="text-left py-2">Supplier</th>
                <th className="text-left py-2">Route</th>
                <th className="text-right py-2">Attempts</th>
                <th className="text-right py-2">Errors</th>
                <th className="text-right py-2">Error Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topFailing.map((r, i) => (
                <tr key={i} data-testid={`failing-route-${i}`}>
                  <td className="py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full"
                            style={{ background: (SUPPLIER_META[r.supplier] || {}).color || '#64748b' }} />
                      {(SUPPLIER_META[r.supplier] || {}).label || r.supplier}
                    </span>
                  </td>
                  <td className="py-2 font-mono">{r.route}</td>
                  <td className="py-2 text-right">{r.attempts}</td>
                  <td className="py-2 text-right text-red-600">{r.errors}</td>
                  <td className="py-2 text-right font-semibold">{r.error_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center text-gray-400 text-sm py-6">
            No failing routes — every supplier is healthy in this window.
          </p>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
